import Konva from 'konva';
import * as buttons from 'smg/buttons';
import { AddCartItemParamsUpload, AddCartItems } from 'smg/customer-api/v2/carts';
import { URLAsDataURL } from 'smg/download';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { URLToBlob } from 'smg/util';
import * as models from 'smg/customer-api/models';
import { Relref } from '../hugo';
import * as stockitems from '../stockitems';

declare interface myWindow extends Window {
    readonly relref: string;
    readonly sweaterOrnamentStockImages: Record<string, string>;
    readonly fontStackFill: string;
}
declare const window: myWindow;

const emojiRegex = /(\p{Emoji_Presentation}+)/ug;

void main();

async function main() {
    stockitems.InitGallery();
    stockitems.InitSeeMore();

    const $modalCanvasParent = template.Clone('#modal-canvas-parent-template').firstElementChild as HTMLElement;
    $modalCanvasParent.style.position = 'absolute';
    $modalCanvasParent.style.top = '-10000px';
    $modalCanvasParent.style.left = '-10000px';
    document.body.appendChild($modalCanvasParent);

    const ornamentWidth = 1999;
    const ornamentHeight = 2000;

    const defaultColors = {
        blue: '#243746',    // Pantone 7546C
        red: '#8e2a2a',     // Pantone 7623C
        green: '#36563d',   // Pantone 7735C
        gold: '#bd9a5f',    // Pantone 7562C
        white: '#f6f5f0',
    };

    const defaultTopText = 'Merry Christmas!';
    const defaultBottomText = new Date().getFullYear().toString();

    let mainColor = defaultColors.red;
    let cuffsColor = defaultColors.green;
    let textColor = defaultColors.white;

    let topTextString = '';
    let bottomTextString = '';

    const $dropContainersParent = document.querySelector('#drop-containers-parent') as HTMLDivElement;

    $dropContainersParent.append(template.Clone('#drop-container-front-template'));

    const $form = document.querySelector('#stock-item-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;

    const $topText = document.querySelector('#front-top-text') as HTMLTextAreaElement;
    const $bottomText = document.querySelector('#front-bottom-text') as HTMLTextAreaElement;
    const $colorInputsText = document.querySelector('#color-inputs-text') as HTMLDivElement;

    const $textColorRadioCustom = $colorInputsText.querySelector('#textColorCustom') as HTMLInputElement;
    const $textColorCustomPicker = $colorInputsText.querySelector('#textColorCustomPicker') as HTMLInputElement;

    const $colorInputs = document.querySelector('#color-inputs') as HTMLDivElement;
    const $colorInputsMain = $colorInputs.querySelector('#color-inputs-main') as HTMLDivElement;
    const $colorInputsCuffs = $colorInputs.querySelector('#color-inputs-cuffs') as HTMLDivElement;

    const $mainColorRadioCustom = $colorInputsMain.querySelector('#mainColorCustom') as HTMLInputElement;
    const $mainColorCustomPicker = $colorInputsMain.querySelector('#mainColorCustomPicker') as HTMLInputElement;

    const $cuffsColorRadioCustom = $colorInputsCuffs.querySelector('#cuffsColorCustom') as HTMLInputElement;
    const $cuffsColorCustomPicker = $colorInputsCuffs.querySelector('#cuffsColorCustomPicker') as HTMLInputElement;

    const $sleeveSelect = document.querySelector('#sleeve-select') as HTMLSelectElement;
    const $centerImageSelect = document.querySelector('#center-image-select') as HTMLSelectElement;

    const $quantity = document.querySelector('#quantity') as HTMLInputElement;
    const $addToCart = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const $personalize = document.querySelector('#personalize') as HTMLButtonElement;
    const $reopenEditor = document.querySelector('#reopen-editor-button-parent') as HTMLDivElement;

    const $modalDivider = document.querySelector('#modal-divider') as HTMLDivElement;

    const $canvasContainer = document.querySelector('#canvas-container') as HTMLDivElement;
    const $modalMainRow = document.querySelector('#modal-main-row') as HTMLDivElement;

    const $preview = document.querySelector('#preview-image-parent') as HTMLDivElement;

    const $fields = document.querySelector('#modal-right') as HTMLElement;

    const $editorButtons = document.querySelector('#editor-buttons') as HTMLDivElement;

    const $navAlert = document.querySelector('#alert-nav-no-img') as HTMLDivElement;

    const $metaThemeColor = document.querySelector("meta[name='theme-color']") as HTMLMetaElement;
    const initialThemeColor = $metaThemeColor.content;

    const placeholderImg = `${Relref('/')}img/no-minimum-custom-coins/placeholder-image.png`;

    let modalActive = false;
    let imgUploaded = false;

    let editedTextTop = false;
    let editedTextBottom = false;

    let firstOpen = true;

    buttons.Start($personalize);

    const $canvasParent = document.querySelector('#canvas-parent') as HTMLDivElement;

    const $canvasUploaderLabel = document.querySelector('#canvas-uploader-front') as HTMLLabelElement;

    document.querySelector('#close-alert-nav')?.addEventListener('click', () => $navAlert.classList.add('d-none'));
    $quantity.parentElement?.classList.add('d-none');
    $addToCart.classList.add('d-none');

    // Define an interface for "editor state" to hold all data needed to reset the editor.
    /**
     * Holds all the data needed to recreate a state of the modal editor.
    */
    interface editorState {
        textTop: string;
        textBottom: string;

        editedTextTop: boolean;
        editedTextBottom: boolean;

        customImgUploaded: boolean;

        customImage: {
            /** Image src blob URL */
            imageSrc: string;
            posX: number;
            posY: number;
            width: number;
            height: number;
            scaleX: number;
            scaleY: number;
            uploadID: number;
        };

        sleeveOption: string;

        mainColor: string;
        cuffsColor: string;
        textColor: string;
    }

    const stage = new Konva.Stage({
        container: $canvasParent,
    });

    const bgImage = await new Promise<Konva.Image>(resolve => {
        const img = new Image();
        img.onload = () =>
            resolve(new Konva.Image({
                image: img,
                draggable: false,
                listening: false,
            }));
        img.src = `${Relref('/')}img/custom-sweater-ornaments/background.png`;
    });

    const bgLayer = new Konva.Layer();
    bgLayer.id('bgLayer');

    const bgGroup = new Konva.Group();
    bgGroup.add(bgImage);

    bgLayer.add(bgGroup);
    stage.add(bgLayer);

    let scale = 1;

    function fitStageIntoParentContainer(firstRun = true) {
        const $stageChildren: ChildNode[] = [];
        if (!firstRun) {
            $stageChildren.push(...Array.from($canvasContainer.childNodes));
            $canvasContainer.innerHTML = '';
        }

        if (modalActive) {
            stage.width($canvasContainer.offsetWidth * (ornamentWidth / ornamentHeight));
            stage.height(stage.width() * (ornamentHeight / ornamentWidth));
            scale = (($canvasContainer.offsetWidth * (ornamentWidth / ornamentHeight))) / bgImage.width();
        } else {
            stage.width(ornamentWidth);
            stage.height(ornamentHeight);
            scale = 1;
        }

        stage.scale({ x: scale, y: scale });

        if ($stageChildren) {
            for (const c of $stageChildren) {
                $canvasContainer.appendChild(c);
            }
        }

        stage.offsetX(0);
        stage.offsetY(0);

        $canvasContainer.setAttribute('style', `--scale:${scale};--offsetY:0px;--offsetX:0px`);
    }

    const colorLayer = new Konva.Layer();

    // COLOR LAYER: Main Background Color
    const $mainColorLayerTemplate = document.querySelector('#sweater-main-color-template') as HTMLTemplateElement;

    const $mainColorLayerSVG = template.Clone($mainColorLayerTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $mainColorLayerSVGPath = $mainColorLayerSVG.querySelector('#artwork') as SVGGElement;
    $mainColorLayerSVGPath.style.fill = mainColor;

    const getMainColorLayerSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$mainColorLayerSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: mainColorLayerImg, promise: mainColorLayerPromise } = getMainColorLayerSVGImage();
    const mainColorLayerImage = new Konva.Image({
        image: mainColorLayerImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'mainColorLayerImage',
    });
    const mainColorGroup = new Konva.Group();
    mainColorGroup.id('mainColorLayer');
    mainColorGroup.add(mainColorLayerImage);
    colorLayer.add(mainColorGroup);

    async function updateMainColorLayer(color: string) {
        mainColor = color;
        $mainColorLayerSVGPath.style.fill = mainColor;
        const mainColorFunc = getMainColorLayerSVGImage();
        await mainColorFunc.promise;
        mainColorLayerImage.image(mainColorFunc.img);
        updateTextColors(textColor);
    }

    $colorInputsMain.addEventListener('click', e => {
        const target = e.target as HTMLInputElement;
        if (target.id === 'mainColorCustomPicker') {
            $mainColorRadioCustom.checked = true;
            void updateMainColorLayer($mainColorCustomPicker.value);
        }
        if (target.name === 'mainColor') {
            if (target.id === 'mainColorCustom') {
                $mainColorCustomPicker.click();
            } else {
                void updateMainColorLayer(target.value);
            }
        }
    });

    $mainColorCustomPicker.addEventListener('input', () => {
        void updateMainColorLayer($mainColorCustomPicker.value);
    });
    // END COLOR LAYER: Main Background Color

    // COLOR LAYER: Cuffs Color
    const $cuffsColorLayerTemplate = document.querySelector('#sweater-cuffs-color-template') as HTMLTemplateElement;

    const $cuffsColorLayerSVG = template.Clone($cuffsColorLayerTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $cuffsColorLayerSVGPath = $cuffsColorLayerSVG.querySelector('#artwork') as SVGGElement;
    $cuffsColorLayerSVGPath.style.fill = cuffsColor;

    const getCuffsColorLayerSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$cuffsColorLayerSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: cuffsColorLayerImg, promise: cuffsColorLayerPromise } = getCuffsColorLayerSVGImage();
    const cuffsColorLayerImage = new Konva.Image({
        image: cuffsColorLayerImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'cuffsColorLayerImage',
    });
    const cuffsColorGroup = new Konva.Group();
    cuffsColorGroup.id('cuffsColorLayer');
    cuffsColorGroup.add(cuffsColorLayerImage);
    colorLayer.add(cuffsColorGroup);

    const $trimColorLayerTemplate = document.querySelector('#sweater-trim-color-template') as HTMLTemplateElement;

    const $trimColorLayerSVG = template.Clone($trimColorLayerTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $trimColorLayerSVGPath = $trimColorLayerSVG.querySelector('#artwork') as SVGGElement;
    $trimColorLayerSVGPath.style.fill = darkenHexColor(cuffsColor);
    const getTrimColorLayerSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$trimColorLayerSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: trimColorLayerImg, promise: trimColorLayerPromise } = getTrimColorLayerSVGImage();
    const trimColorLayerImage = new Konva.Image({
        image: trimColorLayerImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'trimColorLayerImage',
    });
    const trimColorGroup = new Konva.Group();
    trimColorGroup.id('trimColorLayer');
    trimColorGroup.add(trimColorLayerImage);
    colorLayer.add(trimColorGroup);

    async function updateCuffsColorLayer(color: string) {
        cuffsColor = color;
        $cuffsColorLayerSVGPath.style.fill = cuffsColor;
        $trimColorLayerSVGPath.style.fill = darkenHexColor(cuffsColor);
        const cuffsFunc = getCuffsColorLayerSVGImage();
        const trimFunc = getTrimColorLayerSVGImage();
        await Promise.all([cuffsFunc.promise, trimFunc.promise]);
        cuffsColorLayerImage.image(cuffsFunc.img);
        trimColorLayerImage.image(trimFunc.img);
    }

    $colorInputsCuffs.addEventListener('click', e => {
        const target = e.target as HTMLInputElement;
        if (target.id === 'cuffsColorCustomPicker') {
            $cuffsColorRadioCustom.checked = true;
            void updateCuffsColorLayer($cuffsColorCustomPicker.value);
        }
        if (target.name === 'cuffsColor') {
            if (target.id === 'cuffsColorCustom') {
                $cuffsColorCustomPicker.click();
            } else {
                void updateCuffsColorLayer(target.value);
            }
        }
    });

    $cuffsColorCustomPicker.addEventListener('input', () => {
        void updateCuffsColorLayer($cuffsColorCustomPicker.value);
    });
    // END COLOR LAYER: Cuffs Color

    stage.add(colorLayer);

    const decorationLayer = new Konva.Layer();

    // DECORATION LAYER: Sleeve
    const $sleeve1LayerTemplate = document.querySelector('#sweater-sleeve-1-template') as HTMLTemplateElement;
    const $sleeve2LayerTemplate = document.querySelector('#sweater-sleeve-2-template') as HTMLTemplateElement;
    const $sleeve3LayerTemplate = document.querySelector('#sweater-sleeve-3-template') as HTMLTemplateElement;
    const $sleeve4LayerTemplate = document.querySelector('#sweater-sleeve-4-template') as HTMLTemplateElement;
    const $sleeve5LayerTemplate = document.querySelector('#sweater-sleeve-5-template') as HTMLTemplateElement;
    const $sleeve6LayerTemplate = document.querySelector('#sweater-sleeve-6-template') as HTMLTemplateElement;
    const $sleeve7LayerTemplate = document.querySelector('#sweater-sleeve-7-template') as HTMLTemplateElement;
    const $sleeve8LayerTemplate = document.querySelector('#sweater-sleeve-8-template') as HTMLTemplateElement;

    const getSleeveLayerSVGImage = (sleeveOption?: string) => {
        let $sleeveLayerSVG: SVGElement | undefined;
        let $sleeveTemplate: HTMLTemplateElement | undefined;
        switch (sleeveOption) {
        case 'sleeve-1':
            $sleeveTemplate = $sleeve1LayerTemplate;
            break;
        case 'sleeve-2':
            $sleeveTemplate = $sleeve2LayerTemplate;
            break;
        case 'sleeve-3':
            $sleeveTemplate = $sleeve3LayerTemplate;
            break;
        case 'sleeve-4':
            $sleeveTemplate = $sleeve4LayerTemplate;
            break;
        case 'sleeve-5':
            $sleeveTemplate = $sleeve5LayerTemplate;
            break;
        case 'sleeve-6':
            $sleeveTemplate = $sleeve6LayerTemplate;
            break;
        case 'sleeve-7':
            $sleeveTemplate = $sleeve7LayerTemplate;
            break;
        case 'sleeve-8':
            $sleeveTemplate = $sleeve8LayerTemplate;
            break;
        }
        $sleeveLayerSVG = undefined;
        if ($sleeveTemplate) {
            $sleeveLayerSVG = template.Clone($sleeveTemplate).firstElementChild?.querySelector('svg') as SVGElement;
        }
        const img = new Image();
        if (!$sleeveLayerSVG) {
            return { img, promise: Promise.resolve() };
        }
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$sleeveLayerSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: sleeveLayerImg, promise: sleeveLayerPromise } = getSleeveLayerSVGImage();
    const sleeveLayerImage = new Konva.Image({
        image: sleeveLayerImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'sleeveLayerImage',
    });
    const sleeveDecorationGroup = new Konva.Group();
    sleeveDecorationGroup.id('sleeveLayer');
    sleeveDecorationGroup.add(sleeveLayerImage);
    decorationLayer.add(sleeveDecorationGroup);

    async function updateSleeveLayer(sleeveOption?: string) {
        $sleeveSelect.value = sleeveOption || '';
        const sleeveFunc = getSleeveLayerSVGImage(sleeveOption);
        await sleeveFunc.promise;
        sleeveLayerImage.image(sleeveFunc.img);
    }

    $sleeveSelect.addEventListener('input', () => {
        void updateSleeveLayer($sleeveSelect.value);
    });
    // END COLOR LAYER: Sleeve

    const fontURL = `//${window.location.host}${window.fontStackFill}`;
    const $fontStyleString = `@font-face {
        font-family:&quot;my-custom-font&quot;;
        src:url(&quot;${html.Escape(await URLAsDataURL(fontURL))}&quot;);
        font-display:auto;font-style:normal;font-weight:400;font-stretch:normal;
    }`;

    // TEXT LAYER: Top Text
    const $topTextTemplate = document.querySelector('#sweater-text-svg-top-template') as HTMLTemplateElement;
    const $topTextSVG = template.Clone($topTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $topTextSVGText = $topTextSVG.querySelector('#topText') as SVGTextPathElement;
    $topTextSVGText.textContent = defaultTopText;

    const $topStyle = $topTextSVG.querySelector('#style2') as HTMLStyleElement;
    $topStyle.innerHTML = $fontStyleString;

    const getTextTopSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$topTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: topImg, promise: topPromise } = getTextTopSVGImage();
    const textTopImage = new Konva.Image({
        image: topImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'textTopImage',
    });
    const textTopGroup = new Konva.Group();
    textTopGroup.id('textTopLayer');
    textTopGroup.add(textTopImage);
    decorationLayer.add(textTopGroup);

    async function updateTopText(inputText: string) {
        topTextString = inputText;
        topTextString = topTextString.replaceAll(emojiRegex, match => '<tspan class="emoji">' + match + '</tspan>');
        $topTextSVGText.innerHTML = topTextString;
        const textFunc = getTextTopSVGImage();
        await textFunc.promise;
        textTopImage.image(textFunc.img);
    }

    $topText.addEventListener('input', () => {
        editedTextTop = true;
        void updateTopText($topText.value);
    });
    // END TEXT LAYER: Top Text

    // TEXT LAYER: Bottom Text
    const $bottomTextTemplate = document.querySelector('#sweater-text-svg-bottom-template') as HTMLTemplateElement;
    const $bottomTextSVG = template.Clone($bottomTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $bottomTextSVGText = $bottomTextSVG.querySelector('#bottomText') as SVGTextPathElement;
    $bottomTextSVGText.textContent = defaultBottomText;

    const $bottomStyle = $bottomTextSVG.querySelector('#style2') as HTMLStyleElement;
    $bottomStyle.innerHTML = $fontStyleString;

    const getTextBottomSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$bottomTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: bottomImg, promise: bottomPromise } = getTextBottomSVGImage();
    const textBottomImage = new Konva.Image({
        image: bottomImg,
        width: ornamentWidth,
        height: ornamentHeight,
        x: 0,
        y: 0,
        id: 'textBottomImage',
    });
    const textBottomGroup = new Konva.Group();
    textBottomGroup.id('textBottomLayer');
    textBottomGroup.add(textBottomImage);
    decorationLayer.add(textBottomGroup);

    async function updateBottomText(inputText: string) {
        bottomTextString = inputText;
        bottomTextString = bottomTextString.replaceAll(emojiRegex, match => '<tspan class="emoji">' + match + '</tspan>');
        $bottomTextSVGText.innerHTML = bottomTextString;
        const textFunc = getTextBottomSVGImage();
        await textFunc.promise;
        textBottomImage.image(textFunc.img);
    }

    $bottomText.addEventListener('input', () => {
        editedTextBottom = true;
        void updateBottomText($bottomText.value);
    });
    // END TEXT LAYER: Bottom Text

    stage.add(decorationLayer);

    for (const key in window.sweaterOrnamentStockImages) {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = key.replace(/-/g, ' ');
        $centerImageSelect.appendChild(option);
    }

    const customOption = document.createElement('option');
    customOption.value = 'custom';
    customOption.textContent = 'Custom';
    $centerImageSelect.appendChild(customOption);
    $centerImageSelect.value = 'custom';

    $centerImageSelect.addEventListener('input', async e => {
        const target = e.target as HTMLSelectElement;
        stopTransformMode();
        if (target.value === 'custom') {
            void addCustomImage('');
        } else {
            if (window.sweaterOrnamentStockImages[target.value]) {
                await uploads.New([
                    {
                        name: target.value,
                        mime: 'image/png',
                        data: await URLToBlob(window.sweaterOrnamentStockImages[target.value]),
                    },
                ]).then(r => {
                    const upload = r[0];
                    if (!upload) {
                        return;
                    }
                    imgUploaded = true;
                    $navAlert.classList.add('d-none');
                    customImgUploadID = upload.uploadID;
                });
                void addCustomImage(window.sweaterOrnamentStockImages[target.value], 'contain');
            }
        }
    });

    $colorInputsText.addEventListener('click', e => {
        const target = e.target as HTMLInputElement;
        if (target.id === 'textColorCustomPicker') {
            $textColorRadioCustom.checked = true;
            updateTextColors($textColorCustomPicker.value);
        }
        if (target.name === 'textColor') {
            if (target.id === 'textColorCustom') {
                $textColorCustomPicker.click();
            } else {
                updateTextColors(target.value);
            }
        }
    });

    $textColorCustomPicker.addEventListener('input', () => {
        updateTextColors($textColorCustomPicker.value);
    });

    function updateTextColors(color: string) {
        textColor = color;
        $topTextSVGText.style.fill = textColor;
        $topTextSVGText.style.stroke = mainColor;
        $bottomTextSVGText.style.fill = textColor;
        $bottomTextSVGText.style.stroke = mainColor;
        void updateTopText(topTextString);
        void updateBottomText(bottomTextString);
    }

    // INITIALIZE EVERYTHING
    void updateMainColorLayer(defaultColors.red);
    void updateCuffsColorLayer(defaultColors.green);
    void updateSleeveLayer('sleeve-1');
    updateTextColors(defaultColors.white);
    void updateTopText(defaultTopText);
    void updateBottomText(defaultBottomText);
    // END INITIALIZE

    const $stageLoader = document.querySelector('#stage-loader') as HTMLElement;

    const showLoading = () => {
        $canvasContainer.style.opacity = '0.5';
        $canvasContainer.style.pointerEvents = 'none';
        $stageLoader.classList.remove('d-none');
    };

    const hideLoading = () => {
        $canvasContainer.style.opacity = '';
        $canvasContainer.style.pointerEvents = '';
        $stageLoader.classList.add('d-none');
    };

    // start front uploader

    let customImgUploadID = 0;
    const uploadLayer = new Konva.Layer({
        id: 'frontUploadLayer',
    });
    const frontUploadImage = new Konva.Image({
        image: new Image(),
        id: 'frontUploadImage',
    });
    stage.add(uploadLayer);

    uploadLayer.add(frontUploadImage);

    const uploadTransformerLayer = new Konva.Layer({
        visible: false,
        id: 'frontUploadTransformerLayer',
    });
    const uploaderTransformer = new Konva.Transformer({
        anchorStroke: '#fff',
        borderStroke: '#1a73e8',
        anchorSize: 9,
        anchorFill: '#1a73e8',
        centeredScaling: true,
        useSingleNodeRotation: false,
        flipEnabled: false,
    });
    uploadTransformerLayer.add(uploaderTransformer);
    stage.add(uploadTransformerLayer);

    const imgTransformerShape = new Konva.Rect({
        draggable: true,
    });
    uploadTransformerLayer.add(imgTransformerShape);
    uploaderTransformer.nodes([imgTransformerShape]);

    const $dropContainer = document.querySelector('#drop-container-front') as HTMLElement;
    const $stageUploadButtonFront = document.querySelector('#stage-upload-button-front') as HTMLButtonElement;
    $stageUploadButtonFront.addEventListener('click', () => $canvasUploaderLabel.click());

    const $reset = document.querySelector('#reset-front') as HTMLButtonElement;
    const $edit = document.querySelector('#edit-front') as HTMLButtonElement;
    const $changeImage = document.querySelector('#change-image-front') as HTMLButtonElement;
    const $done = document.querySelector('#done-front') as HTMLButtonElement;

    const startTransformMode = () => {
        $editorButtons.classList.remove('d-none');
        $edit.classList.add('d-none');
        $reset.classList.remove('d-none');
        $changeImage.classList.remove('d-none');
        $done.classList.remove('d-none');

        $dropContainersParent.classList.add('pe-none');

        uploadTransformerLayer.show();
    };

    const stopTransformMode = () => {
        $reset.classList.add('d-none');
        $changeImage.classList.add('d-none');
        $done.classList.add('d-none');

        $dropContainersParent.classList.remove('pe-none');

        if (imgUploaded || $centerImageSelect.value !== 'custom') {
            $edit.classList.remove('d-none');
        }

        uploadTransformerLayer.hide();
    };

    let setImageSizeAndPosition: () => void;

    $reset.addEventListener('click', e => {
        e.preventDefault();
        setImageSizeAndPosition?.();
    });

    $edit.addEventListener('click', startTransformMode);
    $changeImage.addEventListener('click', () => $canvasUploaderLabel.click());
    $done.addEventListener('click', stopTransformMode);

    let customImg = new Image();
    let imgShape: Konva.Shape;

    const addCustomImage = (blobURL: string, fit: 'cover' | 'contain' = 'cover') => {
        const maskActualWidth = 986;
        const maskActualHeight = 986;
        const maskOffsetX = 506;
        const maskOffsetY = 685;
        customImg = new Image();

        const mask = new Image();
        const promise = new Promise<void>(resolve => {
            if (blobURL === '') {
                if (imgShape) {
                    imgShape.destroy();
                }
                $dropContainer.classList.remove('d-none');
                resolve();
                return;
            }
            mask.onload = async () => {
                const fixedBlobURL = await processUploadFromURL(blobURL);
                customImg.src = fixedBlobURL;
                customImg.setAttribute('style', 'image-orientation:none');
                customImg.onload = () => {
                    const originalRatios = {
                        width: maskActualWidth / customImg.naturalWidth,
                        height: maskActualHeight / customImg.naturalHeight,
                    };

                    // formula for cover:
                    let coverRatio = Math.max(originalRatios.width, originalRatios.height);
                    if (fit === 'contain') {
                        coverRatio = Math.min(originalRatios.width, originalRatios.height);
                    }

                    // result:
                    const newImageWidth = customImg.naturalWidth * coverRatio;
                    const newImageHeight = customImg.naturalHeight * coverRatio;

                    const newImageX = maskOffsetX - (newImageWidth - maskActualWidth) / 2;
                    const newImageY = maskOffsetY - (newImageHeight - maskActualHeight) / 2;

                    imgTransformerShape.setAttrs({
                        x: newImageX,
                        y: newImageY,
                        width: newImageWidth,
                        height: newImageHeight,
                        scaleX: 1,
                        scaleY: 1,
                    });
                    imgTransformerShape.draw();

                    if (imgShape) {
                        imgShape.destroy();
                    }

                    imgShape = new Konva.Shape({
                        sceneFunc: ctx => {
                            ctx.save();
                            ctx.drawImage(mask, -imgShape.x(), -imgShape.y(), mask.naturalWidth, mask.naturalHeight);
                            // TODO typescript version bug?
                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-in';

                            const toRadians = Math.PI / 180;
                            const angleInDeg = uploaderTransformer.rotation();
                            ctx.rotate(angleInDeg * toRadians);
                            ctx.drawImage(customImg, 0, 0, uploaderTransformer.width() / stage.scaleX(), uploaderTransformer.height() / stage.scaleX());
                            ctx.rotate(-angleInDeg * toRadians);

                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-over';
                            ctx.restore();
                        },
                        hitFunc: ctx => {
                            ctx.rect(0, 0, uploaderTransformer.width() / stage.scaleX(), uploaderTransformer.height() / stage.scaleX());
                            ctx.fillStrokeShape(imgShape);
                        },
                    });
                    setImageSizeAndPosition = () => {
                        imgTransformerShape.rotation(0);
                        imgShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        imgTransformerShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        imgTransformerShape.draw();
                    };
                    setImageSizeAndPosition();
                    uploadLayer.add(imgShape);

                    imgTransformerShape.off('dragmove');
                    imgTransformerShape.on('dragmove', () => imgShape.position(imgTransformerShape.position()));

                    imgTransformerShape.off('transform');
                    imgTransformerShape.on('transform', () => imgShape.position(imgTransformerShape.position()));

                    imgShape.on('click', () => startTransformMode);

                    hideLoading();
                    resolve();
                };

                $dropContainer.classList.add('d-none');
                startTransformMode();

                $uploadRequired.forEach($el => $el.classList.add('d-none'));
            };
        });
        mask.src = `${Relref('/')}img/custom-sweater-ornaments/image-mask.png`;
        $canvasContainer.classList.remove('no-edit-btn');
        return promise;
    };

    new uploads.Uploader($canvasUploaderLabel, {
        dropZones: [$dropContainer],
        start: showLoading,
        stop: hideLoading,
        success: async (thumb, uploadID, blobURL) => {
            $navAlert.classList.add('d-none');
            customImgUploadID = uploadID;
            imgUploaded = true;
            $centerImageSelect.value = 'custom';
            await addCustomImage(blobURL);
        },
    });

    // stop uploader

    // Disable hit graph for most Konva layers, should improve performance by disabling mouse event detection.
    bgLayer.listening(false);
    textTopGroup.listening(false);
    textBottomGroup.listening(false);

    let previewImgBlob: string;

    function createPreviewImg() {
        $preview.innerHTML = '';
        $preview.append(template.Clone('#loading'));

        // crop and generate preview images
        scale = 1;
        const $previewImg = new Image();
        previewImgBlob = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: 0,
            y: 0,
            width: ornamentWidth * scale,
            height: ornamentHeight * scale,
        });
        $previewImg.src = previewImgBlob;

        $previewImg.setAttribute('id', 'front-preview-image');

        $preview.append($previewImg);

        $preview.querySelector('#preview-loading-icon')?.remove();
    }

    $canvasParent.firstElementChild?.setAttribute('id', 'editor');

    const $uploadRequired = document.querySelectorAll('.upload-required');

    document.addEventListener('click', e => {
        if (!customImgUploadID) {
            return;
        }

        if (e.target !== $canvasContainer &&
            e.target !== $edit &&
            e.target !== $dropContainersParent &&
            !(e.target instanceof HTMLElement && e.target?.closest('#canvas-container'))) {
            stopTransformMode();
        }
    });

    let prev: editorState = {
        textTop: $topText.value ?? '',
        textBottom: $bottomText.value ?? '',
        editedTextTop: editedTextTop,
        editedTextBottom: editedTextBottom,
        customImgUploaded: imgUploaded,
        customImage: {
            imageSrc: placeholderImg,
            posX: imgTransformerShape.x(),
            posY: imgTransformerShape.y(),
            width: imgTransformerShape.width(),
            height: imgTransformerShape.height(),
            scaleX: imgTransformerShape.scaleX(),
            scaleY: imgTransformerShape.scaleY(),
            uploadID: customImgUploadID ?? 0,
        },
        mainColor: mainColor,
        cuffsColor: cuffsColor,
        sleeveOption: $sleeveSelect.value,
        textColor: textColor,
    };

    function modalEditor() {
        $fields?.classList.remove('d-none');
        $editorButtons?.classList.remove('d-none');
        $modalDivider?.classList.add('d-lg-flex');
        $reopenEditor.classList.add('d-none');

        modalActive = true;
        const prevFirstOpen = firstOpen;

        prev = {
            textTop: $topText.value ?? '',
            textBottom: $bottomText.value ?? '',
            editedTextTop: editedTextTop,
            editedTextBottom: editedTextBottom,
            customImgUploaded: imgUploaded,
            customImage: {
                imageSrc: '',
                posX: imgTransformerShape.x(),
                posY: imgTransformerShape.y(),
                width: imgTransformerShape.width(),
                height: imgTransformerShape.height(),
                scaleX: imgTransformerShape.scaleX(),
                scaleY: imgTransformerShape.scaleY(),
                uploadID: customImgUploadID ?? 0,
            },
            mainColor: mainColor,
            cuffsColor: cuffsColor,
            sleeveOption: $sleeveSelect.value,
            textColor: textColor,
        };

        prev.customImage.imageSrc = customImg.src;

        if (firstOpen) {
            void updateTopText('');
            void updateBottomText('');

            void addCustomImage('').then(stopTransformMode);
        }

        firstOpen = false;

        function selectColorInput($inputGroup: HTMLElement, color: string) {
            const $radioCustom = $inputGroup.querySelector('input[id$="Custom"]') as HTMLInputElement;
            const $customPicker = $inputGroup.querySelector('input[type="color"]') as HTMLInputElement;
            const $radios = $inputGroup.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;

            let foundMatch = false;
            for (const $radio of Array.from($radios)) {
                if ($radio.value.toLowerCase().replace('#', '') === color.toLowerCase().replace('#', '')) {
                    $radio.checked = true;
                    foundMatch = true;
                    break;
                }
            }
            if (!foundMatch) {
                $radioCustom.checked = true;
                $customPicker.value = color;
            }
        }

        selectColorInput($colorInputsMain, mainColor);
        selectColorInput($colorInputsCuffs, cuffsColor);
        selectColorInput($colorInputsText, textColor);

        if (!imgUploaded) {
            $dropContainer?.classList.remove('d-none');
        }

        for (const $el of Array.from($uploadRequired)) {
            $el.classList.add('d-none');
        }

        $modalMainRow.classList.remove('d-none');
        $preview?.classList.add('d-none');

        $modalCanvasParent.removeAttribute('style');

        const exitModal = async (cancel?: boolean) => {
            stopTransformMode();

            $metaThemeColor.content = initialThemeColor;

            $edit?.classList.add('d-none');

            $fields?.classList.add('d-none');
            $editorButtons?.classList.add('d-none');
            $dropContainer?.classList.add('d-none');
            $modalDivider?.classList.remove('d-lg-flex');

            modalActive = false;
            activeModal.close();

            $modalCanvasParent.style.position = 'absolute';
            $modalCanvasParent.style.top = '-10000px';
            $modalCanvasParent.style.left = '-10000px';

            fitStageIntoParentContainer(false);

            if (!editedTextTop) {
                await updateTopText('');
            }
            if (!editedTextBottom) {
                await updateBottomText('');
            }

            $preview?.classList.remove('d-none');

            if (cancel) {
                await resetToPrev().then(() => {
                    createPreviewImg();
                    firstOpen = prevFirstOpen;
                });
            } else {
                createPreviewImg();
                firstOpen = false;
            }

            if (!firstOpen) {
                $personalize.classList.add('d-none');
                $addToCart.classList.remove('d-none');
                $quantity.parentElement?.classList.remove('d-none');
                $reopenEditor.classList.remove('d-none');
            }

            $modalMainRow.classList.add('d-none');
        };

        async function resetToPrev() {
            // Reset front image
            if (prevFirstOpen) {
                await addCustomImage(placeholderImg).then(stopTransformMode);
            } else {
                await addCustomImage(prev.customImage.imageSrc).then(() => {
                    stopTransformMode();
                    if (imgShape) {
                        imgShape.setAttrs({
                            x: prev.customImage.posX,
                            y: prev.customImage.posY,
                        });
                        imgTransformerShape.setAttrs({
                            x: prev.customImage.posX,
                            y: prev.customImage.posY,
                            width: prev.customImage.width,
                            height: prev.customImage.height,
                            scaleX: prev.customImage.scaleX,
                            scaleY: prev.customImage.scaleY,
                        });
                    }
                }).catch();
                imgUploaded = prev.customImgUploaded;
            }
            // Reset Text
            let resetTextTop: string;
            let resetTextBottom: string;

            if (prevFirstOpen) {
                resetTextTop = defaultTopText;
                resetTextBottom = defaultBottomText;
            } else {
                if (prev.editedTextTop) {
                    resetTextTop = prev.textTop;
                } else {
                    resetTextTop = '';
                }
                if (prev.editedTextBottom) {
                    resetTextBottom = prev.textBottom;
                } else {
                    resetTextBottom = '';
                }
            }
            await updateTopText(resetTextTop);
            await updateBottomText(resetTextBottom);
            $topText.value = prev.textTop;
            $bottomText.value = prev.textBottom;

            editedTextTop = prev.editedTextTop;
            editedTextBottom = prev.editedTextBottom;

            // Reset upload IDs
            customImgUploadID = prev.customImage.uploadID;

            // Reset Colors
            updateTextColors(prev.textColor);
            await updateMainColorLayer(prev.mainColor);
            await updateCuffsColorLayer(prev.cuffsColor);
            await updateSleeveLayer(prev.sleeveOption);

            // Should now be fully reset
        }

        const submitBtn = async () => {
            if (!customImgUploadID) {
                $navAlert.classList.remove('d-none');
                return;
            } else {
                await exitModal(false);
            }
        };

        $metaThemeColor.content = '#ffffff';

        const activeModal = new Modal({
            title: 'Customize Your Ornament',
            size: 'modal-lg',
            body: $modalCanvasParent,
            outsideClosable: false,
            escape: false,
            submitAutoFocus: false,
            buttons: [
                {
                    text: 'Complete Ornament Design',
                    class: 'btn btn-secondary d-none d-md-inline text-white btn-modal-complete',
                    click: submitBtn,
                },
                {
                    text: 'Complete',
                    class: 'btn btn-secondary d-md-none text-white btn-modal-complete',
                    click: submitBtn,
                },
                {
                    text: 'Cancel',
                    class: 'btn btn-outline-secondary',
                    click: () => exitModal(true),
                },
            ],
            cancelButton: false,
        });

        // Remove the modal confirm button so we can use ours that checks for uploads before closing
        activeModal.$element.querySelector('#modal-confirm-btn')?.remove();

        const $modalHeader = activeModal.$element.querySelector('.modal-header') as HTMLDivElement;
        $modalHeader.prepend(template.Clone('#modal-sm-back-button'));

        fitStageIntoParentContainer(false);

        if (!imgUploaded) {
            $dropContainer?.classList.remove('d-none');
            $canvasContainer.classList.add('no-edit-btn');
            $editorButtons.classList.add('d-none');
        } else {
            $editorButtons.classList.remove('d-none');
            $edit?.classList.remove('d-none');
            $canvasContainer.classList.remove('no-edit-btn');
        }
    }

    $personalize.addEventListener('click', e => {
        e.preventDefault();
        modalEditor();
    });

    function initPreview() {
        $modalMainRow.classList.remove('d-none');
        $preview?.classList.add('d-none');
        createPreviewImg();
        $modalMainRow.classList.add('d-none');
        $preview?.classList.remove('d-none');
    }

    $reopenEditor.addEventListener('click', e => {
        e.preventDefault();
        modalEditor();
    });

    interface textField {
        name: string;
        value: string;
    }

    function textFieldCartParams(): textField[] {
        const fields: textField[] = [];
        if ($topText.value !== '') {
            fields.push({
                name: 'Top Text',
                value: $topText.value,
            });
        }
        if ($bottomText.value !== '') {
            fields.push({
                name: 'Bottom Text',
                value: $bottomText.value,
            });
        }
        return fields;
    }

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        if (!customImgUploadID) {
            for (const $el of Array.from($uploadRequired)) {
                $el.classList.remove('d-none');
            }
            return;
        }

        if (!ReportValidity($form)) {
            return;
        }

        $fieldset.disabled = true;
        buttons.Start($addToCart);

        // clean up empty inputs
        if (!$topText.value) {
            $topText.dispatchEvent(new Event('input'));
        }
        if (!$bottomText.value) {
            $bottomText.dispatchEvent(new Event('input'));
        }

        bgGroup.hide();

        const frontURL = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: 0,
            y: 0,
            width: ornamentWidth,
            height: ornamentHeight,
            pixelRatio: 1200 / (ornamentHeight * scale),
        });

        bgGroup.show();

        const previewUpload = (await uploads.NewFromURL([{
            name: 'front.png',
            mime: 'image/png',
            url: previewImgBlob,
        }]))[0];

        const stageUploads = await uploads.NewFromURL([
            {
                name: '.front.jpg',
                mime: 'image/jpeg',
                url: await stockitems.ColorLayer(frontURL),
            },
            {
                name: '.front-white.jpg',
                mime: 'image/jpeg',
                url: await stockitems.WhiteLayer(frontURL),
            },
        ]);

        const params: AddCartItemParamsUpload = {
            uploadID: previewUpload.uploadID,
            quantity: parseInt($quantity.value, 10),
            options: textFieldCartParams(),
            cartItemUploadIDs: [customImgUploadID, ...stageUploads.map(u => u.uploadID)],
            cartItemUploads: [
                {
                    uploadID: previewUpload.uploadID,
                    tags: ['proof'],
                },
            ],
            uploadLineItem: {
                name: 'Custom Sweater Ornaments',
                lineItem: 'Ornament',
                type: 'Christmas Sweater',
                size: '3" × 3"',
                networkSKUID: models.NetworkSKUID.NETWORK_SK_US_CUSTOM_SWEATER_ORNAMENTS,
                childLineItems: [],
            },
        };

        AddCartItems(params).then(() => {
            window.location.href = Relref('/cart');
        }).catch(err => {
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            ShowError(err);
        });
    });

    fitStageIntoParentContainer();
    window.addEventListener('resize', () => fitStageIntoParentContainer(false));

    void Promise.all([
        addCustomImage(placeholderImg),
        topPromise,
        bottomPromise,
        mainColorLayerPromise,
        cuffsColorLayerPromise,
        trimColorLayerPromise,
        sleeveLayerPromise,
    ]).then(() => {
        stopTransformMode();
        initPreview();
        buttons.Stop($personalize);
        $preview.querySelector('#preview-loading-icon')?.remove();
    });

    const removePodiumWidget = () => {
        const getPodiumWidget = () => document.querySelector<HTMLElement>('#podium-website-widget');
        if (getPodiumWidget()) {
            getPodiumWidget()?.remove();
        } else {
            setTimeout(removePodiumWidget, 100);
        }
    };
    removePodiumWidget();
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string = 'image/png', quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Canvas toBlob failed'));
            }
        }, type, quality);
    });
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

async function processUploadFromURL(url: string): Promise<string> {
    const img = await loadImage(url);

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')?.drawImage(img, 0, 0);

    const fixedBlob = await canvasToBlob(canvas, 'image/png');
    return URL.createObjectURL(fixedBlob);
}

function darkenHexColor(hex: string): string {
    if (!/^#?([0-9a-fA-F]{3}){1,2}$/.test(hex)) {
        // doesn't look like hex color string
        return hex;
    }
    hex = hex.replace('#', '');
    if (hex.length === 3) {
        // 3-digit hex colors are equal to the six digit color with each digit repeated,
        // e.g. #3cd = #33ccdd
        hex = hex.split('').map(c => c + c).join('');
    }

    const darkenFactor = 0.2;

    // get numbers from hex value
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // darken colors
    r = r - Math.round(r * darkenFactor);
    g = g - Math.round(g * darkenFactor);
    b = b - Math.round(b * darkenFactor);

    let outString = '#';
    outString += r.toString(16).padStart(2, '0');
    outString += g.toString(16).padStart(2, '0');
    outString += b.toString(16).padStart(2, '0');

    return outString;
}